import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { fetchSiteContent, adminSetSiteContent, adminUploadImage } from "@/lib/api";
import AdminHeader from "@/components/admin/AdminHeader";

const assetUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

// Categorized image slots that mirror the exact slots present on the public pages.
// Each key maps 1:1 to a placeholder currently rendered in the codebase.
const SECTIONS = [
  {
    title: "Homepage — Banner 1",
    hint: "Upload the desktop banner image and the mobile banner image. Heading, subheading and CTA are set inside the Homepage component and follow the Utsah design language automatically.",
    fields: [
      { key: "home_hero_1", label: "Desktop Banner Image", type: "image" },
      { key: "home_hero_1_mobile_image", label: "Mobile Banner Image", type: "image" },
    ],
  },
  {
    title: "Homepage — Banner 2",
    hint: "Same as Banner 1. Only the desktop and mobile banner images are editable here.",
    fields: [
      { key: "home_hero_2", label: "Desktop Banner Image", type: "image" },
      { key: "home_hero_2_mobile_image", label: "Mobile Banner Image", type: "image" },
    ],
  },
  {
    title: "Homepage — Other Imagery",
    hint: '"One Gift. One Tree." image + 4 Our Process step cards.',
    fields: [
      { key: "home_tree_image", label: "\"One Gift. One Tree.\" Section Image", type: "image" },
      { key: "home_step_1_image", label: "Our Process — Step 01 Image", type: "image" },
      { key: "home_step_2_image", label: "Our Process — Step 02 Image", type: "image" },
      { key: "home_step_3_image", label: "Our Process — Step 03 Image", type: "image" },
      { key: "home_step_4_image", label: "Our Process — Step 04 Image", type: "image" },
    ],
  },
  {
    title: "Diwali Collection",
    hint: "Two-slide hero carousel — upload desktop + mobile artwork for each slide.",
    fields: [
      { key: "diwali_hero_1", label: "Diwali Hero — Slide 1 (Desktop)", type: "image" },
      { key: "diwali_hero_1_mobile_image", label: "Diwali Hero — Slide 1 (Mobile)", type: "image" },
      { key: "diwali_hero_2", label: "Diwali Hero — Slide 2 (Desktop)", type: "image" },
      { key: "diwali_hero_2_mobile_image", label: "Diwali Hero — Slide 2 (Mobile)", type: "image" },
    ],
  },
  {
    title: "Green Gift",
    hint: "Hero image + Impact Certificate image on the Green Gift page.",
    fields: [
      { key: "green_gift_hero", label: "Green Gift Hero Image", type: "image" },
      { key: "green_gift_certificate_image", label: "Impact Certificate Image", type: "image" },
    ],
  },
  {
    title: "Our Story",
    hint: "Editorial hero (desktop + mobile), founder portrait, and 3 Reliance showcase gallery images.",
    fields: [
      { key: "our_story_hero", label: "Editorial Hero — Desktop", type: "image" },
      { key: "our_story_hero_mobile_image", label: "Editorial Hero — Mobile", type: "image" },
      { key: "our_story_founder", label: "Founder Portrait", type: "image" },
      { key: "our_story_gallery_1", label: "Reliance Showcase — Image 1 (Large)", type: "image" },
      { key: "our_story_gallery_2", label: "Reliance Showcase — Image 2", type: "image" },
      { key: "our_story_gallery_3", label: "Reliance Showcase — Image 3", type: "image" },
    ],
  },
  {
    title: "Partner Benefits",
    hint: "Lifestyle / Product Feature image in the page hero.",
    fields: [
      { key: "partner_benefits_hero", label: "Partner Benefits Hero Image", type: "image" },
    ],
  },
  {
    title: "Homepage — Exclusive Bonus",
    hint: "Single composite artwork containing the partner brand logos. Upload one wide desktop image and one mobile-composed image — they replace the previous individual-logo grid entirely.",
    fields: [
      { key: "home_privilege_image", label: "Exclusive Bonus — Desktop Image", type: "image" },
      { key: "home_privilege_mobile_image", label: "Exclusive Bonus — Mobile Image", type: "image" },
    ],
  },
  {
    title: "Homepage — Diwali 2026 Showcase",
    hint: "Image displayed in the Diwali 2026 section (previously reserved for a video). Upload separate desktop and mobile artwork.",
    fields: [
      { key: "home_diwali_showcase_image", label: "Diwali 2026 — Desktop Image", type: "image" },
      { key: "home_diwali_showcase_mobile_image", label: "Diwali 2026 — Mobile Image", type: "image" },
    ],
  },
  {
    title: "Diwali 2026 Catalogue — Publish Toggle",
    hint: "Enter 'true' to make /diwali-catalogue publicly viewable and enable the PDF download URL. Leave blank (or any other value) to keep it hidden — visitors get a 'Coming soon' message and the PDF endpoint returns 404. Admins can always preview via /diwali-catalogue?preview=1.",
    fields: [
      { key: "catalogue_diwali_2026_published", label: "Published (true / blank)", type: "text" },
    ],
  },
  {
    title: "Diwali 2026 Catalogue — Static Images",
    hint: "Images used across the Diwali 2026 catalogue slides. Any image left blank shows a discreet placeholder on the catalogue — the slide still renders. Recommended dimensions: landscape 4:3 or 3:2 photography for hero/section visuals; portrait 3:4 for sleeve designs.",
    fields: [
      { key: "catalogue_diwali_2026_cover_image", label: "Slide 1 — Cover hero image", type: "image" },
      { key: "catalogue_diwali_2026_collection_visual", label: "Slide 2 — Diwali Collection primary visual", type: "image" },
      { key: "catalogue_diwali_2026_bucket_500_1000_visual", label: "Section opener — ₹500 to ₹1,000 visual", type: "image" },
      { key: "catalogue_diwali_2026_bucket_1000_1500_visual", label: "Section opener — ₹1,000 to ₹1,500 visual", type: "image" },
      { key: "catalogue_diwali_2026_bucket_1500_2000_visual", label: "Section opener — ₹1,500 to ₹2,000 visual", type: "image" },
      { key: "catalogue_diwali_2026_bucket_2000_3000_visual", label: "Section opener — ₹2,000 to ₹3,000 visual", type: "image" },
      { key: "catalogue_diwali_2026_bucket_3000_4000_visual", label: "Section opener — ₹3,000 to ₹4,000 visual", type: "image" },
      { key: "catalogue_diwali_2026_bucket_4000_5000_visual", label: "Section opener — ₹4,000 to ₹5,000 visual", type: "image" },
      { key: "catalogue_diwali_2026_sleeve_1", label: "Designed to Be Yours — Sleeve design 1", type: "image" },
      { key: "catalogue_diwali_2026_sleeve_2", label: "Designed to Be Yours — Sleeve design 2", type: "image" },
      { key: "catalogue_diwali_2026_sleeve_3", label: "Designed to Be Yours — Sleeve design 3", type: "image" },
      { key: "catalogue_diwali_2026_tree_visual", label: "Plant a Tree Initiative — Tree/nature visual", type: "image" },
      { key: "catalogue_diwali_2026_about_utsah_image", label: "About Utsah — Brand visual", type: "image" },
      { key: "catalogue_diwali_2026_reliance_image_1", label: "Reliance Story — Store / shelf image", type: "image" },
      { key: "catalogue_diwali_2026_reliance_image_2", label: "Reliance Story — Reliance store image", type: "image" },
      { key: "catalogue_diwali_2026_closing_image", label: "Thank You slide — Closing/brand image", type: "image" },
    ],
  },
];

export default function AdminSiteContentPage() {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [content, setContent] = useState({});
  const [loadingContent, setLoadingContent] = useState(false);
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(async () => {
    setLoadingContent(true);
    try { setContent(await fetchSiteContent()); }
    catch { toast.error("Failed to load site content"); }
    setLoadingContent(false);
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login");
    else if (user) load();
  }, [user, loading, navigate, load]);

  const uploadImage = async (key, file) => {
    if (!file) return;
    try {
      const { url } = await adminUploadImage(file);
      setContent((prev) => ({ ...prev, [key]: url }));
      await adminSetSiteContent(key, url);
      toast.success("Image updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  const saveText = async (key) => {
    setSavingKey(key);
    try {
      await adminSetSiteContent(key, content[key] || "");
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSavingKey(null);
  };

  const clearImage = async (key) => {
    setSavingKey(key);
    try {
      await adminSetSiteContent(key, "");
      setContent((prev) => ({ ...prev, [key]: "" }));
      toast.success("Cleared — placeholder will show on the site");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    }
    setSavingKey(null);
  };

  if (loading || !user) return <div className="admin-shell"><div className="admin-loading">Loading…</div></div>;

  return (
    <div className="admin-shell utsah-app" data-testid="admin-site-content-page">
      <AdminHeader active="site-content" onLogout={async () => { await logout(); navigate("/admin/login"); }} email={user.email} />
      <div className="admin-container">
        <div className="admin-heading-row">
          <div>
            <div className="eyebrow">CMS</div>
            <h1 className="admin-heading">Site Content</h1>
          </div>
        </div>

        {loadingContent ? (
          <div className="admin-loading">Loading…</div>
        ) : (
          <div className="admin-form">
            {SECTIONS.map((section) => (
              <section key={section.title} className="admin-form-section" data-testid={`site-section-${section.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <h3 className="admin-form-section-title">{section.title}</h3>
                {section.hint ? (
                  <p className="admin-form-hint" style={{ marginBottom: 18 }}>{section.hint}</p>
                ) : null}
                {section.fields.map((field) => {
                  const value = content[field.key] || "";
                  const fieldType = field.type || "image";

                  if (fieldType === "text" || fieldType === "textarea") {
                    const InputTag = fieldType === "textarea" ? "textarea" : "input";
                    return (
                      <div key={field.key} style={{ borderTop: "1px solid rgba(0,0,0,0.08)", paddingTop: 20, marginTop: 20 }}>
                        <div style={{ fontWeight: 600, marginBottom: 10 }}>{field.label}</div>
                        <div className="admin-form-row">
                          <InputTag
                            {...(fieldType === "text" ? { type: "text" } : { rows: 3 })}
                            value={value}
                            onChange={(e) => setContent((prev) => ({ ...prev, [field.key]: e.target.value }))}
                            data-testid={`site-text-${field.key}`}
                            style={fieldType === "textarea" ? { minHeight: 72 } : undefined}
                          />
                          <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                            <button
                              className="admin-nav-btn"
                              disabled={savingKey === field.key}
                              onClick={() => saveText(field.key)}
                              data-testid={`site-save-${field.key}`}>
                              {savingKey === field.key ? "Saving…" : "Save"}
                            </button>
                            {value ? (
                              <button
                                className="admin-nav-btn"
                                disabled={savingKey === field.key}
                                onClick={() => clearImage(field.key)}
                                data-testid={`site-clear-${field.key}`}
                                style={{ background: "transparent", color: "var(--u-orange)" }}>
                                Clear
                              </button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={field.key} style={{ borderTop: "1px solid rgba(0,0,0,0.08)", paddingTop: 20, marginTop: 20 }}>
                      <div style={{ fontWeight: 600, marginBottom: 10 }}>{field.label}</div>

                      {value ? (
                        <div className="admin-image-tile" style={{ maxWidth: 420, marginBottom: 12 }} data-testid={`site-preview-${field.key}`}>
                          <img src={assetUrl(value)} alt={field.label} />
                        </div>
                      ) : (
                        <div style={{ opacity: 0.6, marginBottom: 12, fontSize: 13 }}>No image uploaded — the site will show its default placeholder.</div>
                      )}

                      <div className="admin-form-row">
                        <label>Upload New Image (JPG or PNG)</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => uploadImage(field.key, e.target.files?.[0])}
                          data-testid={`site-upload-${field.key}`}
                        />
                      </div>

                      <div className="admin-form-row">
                        <label>Or paste an external image URL</label>
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => setContent((prev) => ({ ...prev, [field.key]: e.target.value }))}
                          data-testid={`site-url-${field.key}`}
                        />
                        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                          <button
                            className="admin-nav-btn"
                            disabled={savingKey === field.key}
                            onClick={() => saveText(field.key)}
                            data-testid={`site-save-${field.key}`}>
                            {savingKey === field.key ? "Saving…" : "Save URL"}
                          </button>
                          {value ? (
                            <button
                              className="admin-nav-btn"
                              disabled={savingKey === field.key}
                              onClick={() => clearImage(field.key)}
                              data-testid={`site-clear-${field.key}`}
                              style={{ background: "transparent", color: "var(--u-orange)" }}>
                              Clear
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </section>
            ))}
          </div>
        )}
      </div>
      <Toaster position="bottom-center" richColors theme="light" />
    </div>
  );
}
